import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import type { PreflightModel, PreflightState } from '@/lib/preflight-checklist'

const stateStyles: Record<PreflightState, { readonly wrapper: string; readonly badge: string; readonly icon: typeof CheckCircle2 }> = {
  ready: { wrapper: 'border-emerald-200 bg-emerald-50 text-emerald-900', badge: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  warning: { wrapper: 'border-amber-200 bg-amber-50 text-amber-900', badge: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
  missing: { wrapper: 'border-rose-200 bg-rose-50 text-rose-900', badge: 'bg-rose-100 text-rose-800', icon: AlertTriangle },
  disabled: { wrapper: 'border-slate-200 bg-slate-50 text-slate-900', badge: 'bg-slate-100 text-slate-700', icon: Info },
  error: { wrapper: 'border-red-200 bg-red-50 text-red-900', badge: 'bg-red-100 text-red-800', icon: AlertTriangle },
  optional: { wrapper: 'border-slate-200 bg-slate-50 text-slate-900', badge: 'bg-slate-100 text-slate-700', icon: Info },
}

function stateLabel(state: PreflightState): string {
  switch (state) {
    case 'ready': return 'Ready'
    case 'warning': return 'Warning'
    case 'missing': return 'Missing'
    case 'disabled': return 'Disabled'
    case 'error': return 'Error'
    case 'optional': return 'Optional'
  }
}

export function DependencyChecklistCard({
  model,
  title = model.title,
  subtitle = model.description,
}: {
  readonly model: PreflightModel
  readonly title?: string
  readonly subtitle?: string
}) {
  const overall = stateStyles[model.overallState]
  const OverallIcon = overall.icon

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
      <div className={`rounded-lg border p-4 ${overall.wrapper}`}>
        <div className="flex items-start gap-3">
          <OverallIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="text-sm">
              {model.overallState === 'ready'
                ? 'All required prerequisites are satisfied.'
                : model.overallState === 'warning'
                  ? 'Required prerequisites exist, but one or more warnings remain.'
                  : model.overallState === 'disabled'
                    ? 'Required prerequisites are present but disabled.'
                    : model.overallState === 'error'
                      ? 'At least one required prerequisite has an error.'
                      : 'At least one required prerequisite is still missing.'}
            </p>
            <p className="text-xs opacity-80">{subtitle}</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {model.sections.map((section) => (
          <div key={section.title} className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
              <p className="text-xs text-gray-600">{section.description}</p>
            </div>

            {section.items.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">No checklist items.</div>
            ) : (
              <ol className="space-y-2">
                {section.items.map((item) => {
                  const styles = stateStyles[item.state]
                  const Icon = styles.icon

                  return (
                    <li key={item.key} className={`rounded-md border p-4 ${styles.wrapper}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-medium">{item.label}</div>
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${styles.badge}`}>
                              {stateLabel(item.state)}
                            </span>
                            <span className="inline-flex items-center rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                              {item.required ? 'Required' : 'Optional'}
                            </span>
                          </div>
                          <div className="text-sm">{item.summary}</div>
                          {item.details.length > 0 && <div className="text-xs opacity-80">{item.details.join(' ')}</div>}
                        </div>
                        <Link to={item.action.href} className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900">
                          {item.action.label}
                          <Icon className="h-4 w-4" />
                        </Link>
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
